import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Footer } from '../components/Footer';
export function PrivacyPage() {
  return <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-[#0F172A]" data-id="element-449">
      {/* Simple Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-450">
        <div className="container mx-auto px-6 max-w-6xl h-20 flex items-center justify-between" data-id="element-451">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-[#0F172A] no-underline" data-id="element-452">
            <img src="https://assets.corkship.io/CorkShip%20Logo%20Original%20png.png" alt="CorkShip Logo" className="w-8 h-8 object-contain" data-id="element-453" />
            CorkShip
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-[#64748B] hover:text-[#0284C7] transition-colors" data-id="element-454">
            <ArrowLeft size={16} data-id="element-455" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow py-16 px-6" data-id="element-456">
        <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12" data-id="element-457">
          {/* Page Header */}
          <header className="mb-12 text-center md:text-left border-b border-slate-100 pb-8" data-id="element-458">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" data-id="element-459">
              Privacy Policy
            </h1>
            <p className="text-[#64748B] text-lg" data-id="element-460">Last updated: January 2026</p>
          </header>

          {/* Content Body */}
          <div className="max-w-none" data-id="element-461">
            <section className="mb-12" data-id="element-462">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-463">
                Overview
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-464">
                CorkShip ("we", "our", or "us") is an application that helps
                Australian wineries manage their shipping and fulfilment through
                Australia Post's eParcel service. This Privacy Policy explains
                how we collect, use, and protect your information when you use
                our application.
              </p>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-465">
                We are committed to protecting your privacy and handling your
                data transparently. We do not sell your data to third parties.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-466" />

            <section className="mb-12" data-id="element-467">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-468">
                Information We Collect
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-469">
                From Your Platform (when you install the app)
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-470">
                When you install CorkShip on your store, we access the following
                data through your platform's API:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-471">
                <li data-id="element-472">
                  <strong data-id="element-473">Store information:</strong> Shop name, domain, email
                  address, currency, and timezone
                </li>
                <li data-id="element-474">
                  <strong data-id="element-475">Order information:</strong> Order details, customer
                  shipping addresses, product information, and order status
                </li>
                <li data-id="element-476">
                  <strong data-id="element-477">Product information:</strong> Product names, variants,
                  and attributes (to calculate bottle counts)
                </li>
                <li data-id="element-478">
                  <strong data-id="element-479">Customer information:</strong> Names and shipping
                  addresses (for generating shipping labels)
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-480">
                We only access order and customer data for orders you choose to
                process through CorkShip.
              </p>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-481">
                From You (when you configure the app)
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-482">
                <li data-id="element-483">
                  <strong data-id="element-484">Australia Post credentials:</strong> Your eParcel API
                  credentials (Account Number, API Key, API Secret)
                </li>
                <li data-id="element-485">
                  <strong data-id="element-486">Shipping preferences:</strong> Parcel size
                  configurations, default weights, and service preferences
                </li>
                <li data-id="element-487">
                  <strong data-id="element-488">Postcode rules:</strong> Any allowlist or blocklist
                  rules you configure for shipping validation
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-489">
                Automatically Collected
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-490">
                <li data-id="element-491">
                  <strong data-id="element-492">Usage data:</strong> Which features you use and how
                  often (for improving the app)
                </li>
                <li data-id="element-493">
                  <strong data-id="element-494">Error logs:</strong> Technical errors that occur (for
                  troubleshooting and fixing bugs)
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-495" />

            <section className="mb-12" data-id="element-496">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-497">
                How We Use Your Information
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-498">
                We use your information solely to provide the CorkShip service:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-499">
                <li data-id="element-500">
                  <strong data-id="element-501">Processing shipments:</strong> Creating shipping
                  labels and manifests through Australia Post
                </li>
                <li data-id="element-502">
                  <strong data-id="element-503">Syncing data:</strong> Updating your orders with
                  tracking numbers and fulfilment status
                </li>
                <li data-id="element-504">
                  <strong data-id="element-505">Validating addresses:</strong> Checking shipping
                  addresses against Australia Post's address database
                </li>
                <li data-id="element-506">
                  <strong data-id="element-507">Checkout validation:</strong> Validating postcodes at
                  checkout (if enabled)
                </li>
                <li data-id="element-508">
                  <strong data-id="element-509">Improving the service:</strong> Understanding how the
                  app is used to make it better
                </li>
              </ul>

              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-510">We do not:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-511">
                <li data-id="element-512">Sell your data to third parties</li>
                <li data-id="element-513">Use your data for advertising</li>
                <li data-id="element-514">Share your customer lists with anyone</li>
                <li data-id="element-515">
                  Access data from orders you don't process through CorkShip
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-516" />

            <section className="mb-12" data-id="element-517">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-518">
                How We Protect Your Information
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-519">
                Encryption
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-520">
                <li data-id="element-521">
                  All Australia Post credentials are encrypted using AES-256-GCM
                  encryption before storage
                </li>
                <li data-id="element-522">Data is transmitted over HTTPS/TLS</li>
                <li data-id="element-523">Platform access tokens are stored encrypted</li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-524">
                Data Isolation
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-525">
                <li data-id="element-526">
                  Each store's data is completely isolated using database
                  row-level security
                </li>
                <li data-id="element-527">
                  You cannot access another store's data, and they cannot access
                  yours
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-528">
                Infrastructure
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-529">
                <li data-id="element-530">
                  Our application is hosted on Railway (railway.app) with
                  encrypted databases
                </li>
                <li data-id="element-531">Database hosted on PostgreSQL with encryption at rest</li>
                <li data-id="element-532">Regular automated backups</li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-533" />

            <section className="mb-12" data-id="element-534">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-535">
                Data Retention
              </h2>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-536">
                <li data-id="element-537">
                  <strong data-id="element-538">Active accounts:</strong> We retain your data for as
                  long as you have CorkShip installed
                </li>
                <li data-id="element-539">
                  <strong data-id="element-540">After uninstallation:</strong> We delete your data
                  within 30 days of app uninstallation
                </li>
                <li data-id="element-541">
                  <strong data-id="element-542">Order data:</strong> Historical shipment records are
                  kept for your reference while the app is installed
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-543" />

            <section className="mb-12" data-id="element-544">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-545">
                Your Rights
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-546">
                Access and Export
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-547">
                You can request a copy of all data we hold about your store by
                contacting us at{' '}
                <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-548">
                  privacy@corkship.com.au
                </a>
                . We will provide this within 30 days.
              </p>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-549">
                Deletion
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-550">
                You can request deletion of your data at any time by:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-551">
                <li data-id="element-552">
                  Uninstalling the app from your store (data deleted within 30
                  days), or
                </li>
                <li data-id="element-553">
                  Contacting us at{' '}
                  <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-554">
                    privacy@corkship.com.au
                  </a>{' '}
                  for immediate deletion
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-555">
                GDPR Compliance
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-556">
                If you or your customers are in the European Economic Area, you
                have additional rights under GDPR:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-557">
                <li data-id="element-558">Right to access</li>
                <li data-id="element-559">Right to rectification</li>
                <li data-id="element-560">Right to erasure</li>
                <li data-id="element-561">Right to data portability</li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-562">
                We respond to mandatory GDPR webhooks for customer data requests
                and shop data erasure requests.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-563" />

            <section className="mb-12" data-id="element-564">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-565">
                Third-Party Services
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-566">
                CorkShip integrates with the following third-party services to
                provide our functionality:
              </p>

              <div className="space-y-6 mb-6" data-id="element-567">
                <div data-id="element-568">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-569">
                    Shopify / Commerce7
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-570">
                    Purpose: App platform, order and customer data
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-571">
                    Privacy Policy:{' '}
                    <a href="https://www.shopify.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-572">
                      shopify.com/legal/privacy
                    </a>
                    {' | '}
                    <a href="https://commerce7.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-573">
                      commerce7.com/privacy
                    </a>
                  </p>
                </div>

                <div data-id="element-574">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-575">
                    Australia Post
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-576">
                    Purpose: Shipping label generation, address validation,
                    tracking
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-577">
                    Privacy Policy:{' '}
                    <a href="https://auspost.com.au/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-578">
                      https://auspost.com.au/privacy
                    </a>
                  </p>
                </div>

                <div data-id="element-579">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-580">
                    Railway (Hosting)
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-581">
                    Purpose: Application and database hosting
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-582">
                    Privacy Policy:{' '}
                    <a href="https://railway.app/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-583">
                      https://railway.app/legal/privacy
                    </a>
                  </p>
                </div>
              </div>

              <p className="text-[#0F172A] leading-[1.8]" data-id="element-584">
                We do not share your data with any other third parties.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-585" />

            <section className="mb-12" data-id="element-586">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-587">
                Cookies
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-588">
                CorkShip uses essential cookies only:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-589">
                <li data-id="element-590">
                  <strong data-id="element-591">Session cookies:</strong> To keep you logged in while
                  using the app within your store admin
                </li>
                <li data-id="element-592">
                  <strong data-id="element-593">Security cookies:</strong> To protect against
                  cross-site request forgery
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8]" data-id="element-594">
                We do not use tracking cookies or analytics cookies.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-595" />

            <section className="mb-12" data-id="element-596">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-597">
                Changes to This Policy
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-598">
                We may update this Privacy Policy from time to time. We will
                notify you of any significant changes by:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-599">
                <li data-id="element-600">Posting a notice in the app</li>
                <li data-id="element-601">
                  Updating the "Last updated" date at the top of this policy
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8]" data-id="element-602">
                Continued use of CorkShip after changes constitutes acceptance
                of the updated policy.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-603" />

            <section className="mb-12" data-id="element-604">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-605">
                Contact Us
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-606">
                If you have any questions about this Privacy Policy or how we
                handle your data:
              </p>
              <ul className="list-none space-y-2 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-607">
                <li data-id="element-608">
                  <strong data-id="element-609">Email:</strong>{' '}
                  <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-610">
                    privacy@corkship.com.au
                  </a>
                </li>
                <li data-id="element-611">
                  <strong data-id="element-612">Address:</strong> CorkShip Melbourne, Victoria
                  Australia
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-613" />

            <section data-id="element-614">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-615">
                Summary
              </h2>
              <div className="overflow-x-auto" data-id="element-616">
                <table className="w-full text-left border-collapse mt-6" data-id="element-617">
                  <thead data-id="element-618">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-619">
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-620">
                        What we collect
                      </th>
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-621">Why</th>
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-622">
                        Shared with
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm" data-id="element-623">
                    <tr className="border-b border-slate-100" data-id="element-624">
                      <td className="p-4" data-id="element-625">Store info</td>
                      <td className="p-4" data-id="element-626">To identify your account</td>
                      <td className="p-4" data-id="element-627">No one</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-628">
                      <td className="p-4" data-id="element-629">Order & customer data</td>
                      <td className="p-4" data-id="element-630">To create shipping labels</td>
                      <td className="p-4" data-id="element-631">Australia Post (for shipping)</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-632">
                      <td className="p-4" data-id="element-633">Australia Post credentials</td>
                      <td className="p-4" data-id="element-634">
                        To connect to your eParcel account
                      </td>
                      <td className="p-4" data-id="element-635">Australia Post (authentication)</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-636">
                      <td className="p-4" data-id="element-637">Shipping preferences</td>
                      <td className="p-4" data-id="element-638">To configure your parcel sizes</td>
                      <td className="p-4" data-id="element-639">No one</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-8 font-bold text-center text-lg text-[#0F172A]" data-id="element-640">
                We do not sell your data. Ever.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <Footer data-id="element-641" />
    </div>;
}